import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { auth } from '../config/firebase';

// Types for API responses
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ApiError {
  error: string;
  message: string;
  details?: Array<{
    field: string;
    message: string;
  }>;
}

export interface User {
  uid: string; // Firebase UID
  email: string;
  name: string;
  role: 'admin' | 'user' | 'viewer';
  active: boolean;
  department?: string;
  title?: string;
  emailVerified: boolean;
  createdAt: string;
  lastLoginAt?: string | null;
}

// Evidence Layer Types (Phase 1.5)
export interface FieldSuggestion {
  fieldName: string;
  similarity: number; // 0-100
  reason: string;
}

export interface EvidenceSummary {
  totalEvidence: number;
  totalCapabilities: number;
  totalSources: number;
  highConfidenceCount: number;
  mediumConfidenceCount: number;
  lowConfidenceCount: number;
  capabilitiesByLevel: {
    STRONG?: number;
    MEDIUM?: number;
    WEAK?: number;
  };
  duration?: string; // Duration in milliseconds
}

// Aggregated Evidence Metrics (for dashboard display)
export interface EvidenceMetrics {
  avgConfidence: number; // 0-1 range
  minConfidence: number;
  maxConfidence: number;
  confidenceLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  totalEvidence: number;
  totalCapabilities: number;
  avgEvidencePerRule: number;
  rulesWithHighConf: number;
  rulesWithMediumConf: number;
  rulesWithLowConf: number;
  capabilitiesByLevel: {
    STRONG?: number;
    MEDIUM?: number;
    WEAK?: number;
  };
  topCapabilities: string[];
}

export interface ScanResult {
  ruleId: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  status?: 'pass' | 'fail' | 'error' | 'warning' | 'info' | string;
  category?: string;
  error?: string;
  file?: string;
  line?: number;
  column?: number;
  framework: 'HIPAA' | 'HITRUST' | 'SOC2' | 'PCI-DSS';
  remediation?: string;
  // Evidence Layer (Phase 1.5)
  evidenceSummary?: EvidenceSummary;
  fieldSuggestions?: FieldSuggestion[];
  confidence?: number; // 0.0-1.0 (0-100%)
}

export interface Scan {
  id: string;
  scanId: string;
  userId: string;
  userName?: string;
  
  // Results reference (download from GCS)
  results?: ScanResult[];
  storage?: {
    bucket: string;
    path: string;
    sizeBytes: number;
  };
  
  // Summary (aggregated counts)
  summary: {
    totalRules: number;
    passed: number;
    failed: number;
    errors: number;
    skipped: number;
    bySeverity: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
    failedBySeverity: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
    byCategory: Record<string, number>;
    byFramework: Record<string, number>;
  };
  
  // Risk metrics (essential only)
  risk: {
    overallScore: number;
    riskLevel: string;
    complianceRate: number;
  };
  
  // Evidence metrics (aggregated for dashboard)
  evidenceMetrics?: EvidenceMetrics;
  
  // Flat metadata (no nesting)
  scanType: string;
  plan: string;
  cliVersion: string;
  source: string;
  
  // Timestamps
  status: string;
  timestamp: string;
  createdAt: string;
  
  // Query fields
  _hasCritical?: boolean;
  _complianceLevel?: string;
}

export interface DashboardAnalytics {
  summary: {
    totalScans: number;
    totalIssues: number;
    avgRiskScore: number;
    maxRiskScore: number;
    dateRange: {
      start: string;
      end: string;
    };
  };
  severityBreakdown: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  frameworkBreakdown: Record<string, number>;
  scanTypeBreakdown: Record<string, number>;
  complianceStatus: Record<string, {
    totalIssues: number;
    criticalIssues: number;
    status: 'compliant' | 'non-compliant' | 'needs-attention';
  }>;
  recentScans: Array<{
    scanId: string;
    riskScore: number;
    totalIssues: number;
    timestamp: string;
    scanType: string;
  }>;
  generatedAt: string;
}

export interface TrendAnalytics {
  trends: Array<{
    period: string;
    scans: number;
    totalIssues: number;
    avgRiskScore: number;
    severityBreakdown: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
  }>;
  trendIndicators: {
    riskScore?: {
      current: number;
      previous: number;
      change: number;
      trend: 'increasing' | 'decreasing' | 'stable';
    };
    totalIssues?: {
      current: number;
      previous: number;
      change: number;
      trend: 'increasing' | 'decreasing' | 'stable';
    };
  };
  summary: {
    totalPeriods: number;
    granularity: string;
    dateRange: {
      start: string;
      end: string;
    };
  };
  generatedAt: string;
}

export interface ComplianceAnalytics {
  summary: {
    totalFrameworks: number;
    compliantFrameworks: number;
    avgComplianceScore: number;
    totalIssues: number;
  };
  frameworks: Record<string, {
    framework: string;
    totalIssues: number;
    complianceScore: number;
    status: 'compliant' | 'non-compliant' | 'needs-attention' | 'minor-issues';
    severityDistribution: {
      critical: number;
      high: number;
      medium: number;
      low: number;
    };
    topRules: Array<{
      ruleId: string;
      title: string;
      severity: string;
      count: number;
      affectedFiles: number;
    }>;
    recentScans: Array<{
      scanId: string;
      timestamp: string;
      issues: number;
      riskScore: number;
    }>;
  }>;
  dateRange: {
    start: string;
    end: string;
  };
  generatedAt: string;
}

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

// Removed legacy multi-tenant RegisterRequest in favor of single-user RegisterUserRequest

export interface RegisterUserRequest {
  name: string;
  email: string;
  password: string;
}

export interface AuthUser {
  uid: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'viewer';
  active: boolean;
  emailVerified: boolean;
  lastLoginAt?: string;
  createdAt: string;
}

export interface CliTokenStatus {
  hasActiveToken: boolean;
  expiresAt: string | null;
  status: string;
  isSubscribed: boolean;
  canIssue: boolean;
  canRotate?: boolean;
  token?: string | null;
}

export interface IssueCliTokenResponse {
  token: string;
  expiresAt: string;
  status: string;
}

// Billing/Plans
export interface Plan {
  id: string;
  name: string;
  priceMonthlyUsd?: number;
  includedScans?: number;
  overageUsdPerScan?: number;
  softCapScans?: number;
}

export interface UsageSummary {
  monthKey: string;
  periodStart: string;
  periodEnd: string;
  allowedScans: number;
  usedScans: number;
  remainingScans: number;
  planId: string;
}

export interface BillingMeResponse {
  plan: Plan;
  usage: UsageSummary;
}

// Subscription types
export interface PricingTier {
  id: string;
  name: string;
  description: string;
  popular: boolean;
  pricing: {
    monthly: number;
    annual: number;
  };
  limits: {
    scans: {
      included: number;
      overageAllowed: boolean;
      overageRate: number;
    };
    teamSize: {
      recommended: string;
      note: string;
    };
    securityRules: {
      count: number | string;
      type: string;
    };
    customRules: {
      perMonth: number;
    };
    plugins: {
      count: number | string;
      note?: string;
    };
  };
  features: {
    outputFormats: string[];
    advancedAnalytics: boolean;
    dashboardAccess: boolean;
    detailedRemediation: boolean;
    teamManagement: boolean;
    apiAccess: boolean;
    whiteLabel?: boolean;
  };
  support: {
    type: string;
    responseTime: string;
  };
  restrictions?: {
    commercialUse: boolean;
    description: string;
  };
}

export interface RazorpayOrderResponse {
  orderId: string;
  amount: number;       // smallest currency unit (cents for USD)
  currency: string;
  keyId: string;
  tierId: string;
  billingCycle: string;
  development?: boolean;
  message?: string;
}

export interface RazorpayVerifyPaymentRequest {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
  tierId: string;
  billingCycle: string;
}

export interface RazorpayVerifyPaymentResponse {
  message: string;
  subscription: {
    tierId: string;
    billingCycle: string;
    status: string;
    currentPeriodEnd: string;
  };
}

export interface CurrentSubscription {
  subscription: {
    userId: string;
    tierId: string;
    billingCycle: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
  };
  tier: PricingTier;
  usage: {
    scans: {
      current: number;
      included: number;
      overage: number;
    };
  };
}

export interface FalsePositiveReport {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  scanId: string;
  scanDocId: string;
  ruleId: string;
  reason: 'not_applicable' | 'incorrect_detection' | 'environment_specific' | 'other';
  description?: string;
  context?: {
    file?: string;
    line?: number;
    column?: number;
    framework?: string;
    additionalInfo?: string;
  };
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReportFalsePositiveRequest {
  scanId: string;
  ruleId: string;
  ruleName: string; // Required by backend
  ruleDescription?: string; // Optional
  reason: 'not_applicable' | 'incorrect_detection' | 'environment_specific' | 'other';
  description?: string;
  context?: {
    file?: string;
    line?: number;
    column?: number;
    framework?: string;
    additionalInfo?: string;
  };
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api-f2xtf26vda-uc.a.run.app',
      timeout: 30000,
    });

    // Add request interceptor to include auth token
    this.client.interceptors.request.use(
      async (config) => {
        // Do not attach auth for public auth endpoints
        const url = (config.url || '').toString();
        const publicAuthPaths = [
          '/v1/auth/login',
          '/v1/auth/register-user',
          '/v1/auth/forgot-password',
        ];
        const isPublicAuthEndpoint = publicAuthPaths.some((p) => url.startsWith(p));
        if (!isPublicAuthEndpoint) {
          const user = auth.currentUser;
          if (user) {
            // Force token refresh for /me so email verification status is up to date
            const forceRefresh = url.includes('/v1/auth/me');
            const token = await user.getIdToken(forceRefresh);
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const currentPath = window.location.pathname;
        const authPaths = ['/login', '/register', '/forgot-password', '/verify-email'];
        const onAuthPage = authPaths.some((p) => currentPath.includes(p));

        if (error.response?.status === 401 && !onAuthPage) {
          window.location.href = '/login';
        }
        if (error.response?.status === 403 && error.response?.data?.code === 'EMAIL_NOT_VERIFIED' && !currentPath.includes('/verify-email')) {
          window.location.href = '/verify-email';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(loginData: LoginRequest): Promise<{ user: AuthUser; message: string; rememberMe: boolean }> {
    const response: AxiosResponse<ApiResponse<{ user: AuthUser; message: string; rememberMe: boolean }>> = 
      await this.client.post('/v1/auth/login', loginData);
    return response.data.data;
  }

  async registerUser(registerData: RegisterUserRequest): Promise<{ user: AuthUser; message: string }> {
    const response: AxiosResponse<ApiResponse<{ user: AuthUser; message: string }>> =
      await this.client.post('/v1/auth/register-user', registerData);
    return response.data.data;
  }

  async logout(): Promise<{ message: string }> {
    const response: AxiosResponse<ApiResponse<{ message: string }>> = 
      await this.client.post('/v1/auth/logout');
    return response.data.data;
  }

  async getCurrentUser(): Promise<AuthUser> {
    const response: AxiosResponse<ApiResponse<AuthUser>> = 
      await this.client.get('/v1/auth/me', {
        // Avoid cached 304 responses from intermediaries/browsers
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
        params: { ts: Date.now() },
      });
    return response.data.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response: AxiosResponse<ApiResponse<{ message: string }>> = 
      await this.client.post('/v1/auth/forgot-password', { email });
    return response.data.data;
  }

  // Users
  async getUsers(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    filter?: string;
  }): Promise<{ data: User[]; meta: any }> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.client.get('/v1/users', { params });
    return { data: response.data.data, meta: response.data.meta };
  }

  async createUser(userData: {
    email: string;
    name: string;
    role: 'admin' | 'user' | 'viewer';
    department?: string;
    title?: string;
  }): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.client.post('/v1/users', userData);
    return response.data.data;
  }

  async getUser(uid: string): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.client.get(`/v1/users/${uid}`);
    return response.data.data;
  }

  async updateUser(uid: string, data: Partial<User>): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.client.put(`/v1/users/${uid}`, data);
    return response.data.data;
  }

  async deleteUser(uid: string): Promise<void> {
    await this.client.delete(`/v1/users/${uid}`);
  }

  // Scans
  async getScans(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    filter?: string;
  }): Promise<{ scans: Scan[]; meta: any }> {
    const response: AxiosResponse<ApiResponse<Scan[]>> = await this.client.get('/v1/scans', { params });
    return { scans: response.data.data, meta: response.data.meta };
  }

  async getScan(id: string): Promise<Scan> {
    const response: AxiosResponse<ApiResponse<Scan>> = await this.client.get(`/v1/scans/${id}`);
    return response.data.data;
  }

  async getScanResultsUrl(id: string, opts?: { download?: boolean }): Promise<{ url: string; expiresAt: string; contentType?: string; contentEncoding?: string; sizeBytes?: number }> {
    const response: AxiosResponse<ApiResponse<{ url: string; expiresAt: string; contentType?: string; contentEncoding?: string; sizeBytes?: number }>> =
      await this.client.get(`/v1/scans/${id}/results-url`, {
        params: opts?.download ? { download: 1 } : undefined
      });
    return response.data.data;
  }

  async getScanResults(id: string): Promise<any> {
    const response: AxiosResponse<any> = await this.client.get(`/v1/scans/${id}/results`, {
      responseType: 'json',
    });
    return response.data;
  }

  getScanResultsDownloadUrl(id: string): string {
    const baseURL = this.client.defaults.baseURL || '';
    return `${baseURL}/v1/scans/${id}/results?download=1`;
  }

  async deleteScan(id: string): Promise<void> {
    await this.client.delete(`/v1/scans/${id}`);
  }

  // Analytics
  async getDashboardAnalytics(params?: {
    startDate?: string;
    endDate?: string;
    framework?: string;
  }): Promise<DashboardAnalytics> {
    const response: AxiosResponse<ApiResponse<DashboardAnalytics>> = await this.client.get('/v1/analytics/dashboard', { params });
    return response.data.data;
  }

  async getTrendAnalytics(params?: {
    startDate?: string;
    endDate?: string;
    framework?: string;
    granularity?: 'day' | 'week' | 'month';
  }): Promise<TrendAnalytics> {
    const response: AxiosResponse<ApiResponse<TrendAnalytics>> = await this.client.get('/v1/analytics/trends', { params });
    return response.data.data;
  }

  async getComplianceAnalytics(params?: {
    startDate?: string;
    endDate?: string;
    framework?: string;
  }): Promise<ComplianceAnalytics> {
    const response: AxiosResponse<ApiResponse<ComplianceAnalytics>> = await this.client.get('/v1/analytics/compliance', { params });
    return response.data.data;
  }

  // CLI Token endpoints
  async getCurrentCliTokenStatus(): Promise<CliTokenStatus> {
    const response: AxiosResponse<ApiResponse<CliTokenStatus>> = await this.client.get('/v1/tokens/current-cli');
    return response.data.data;
  }

  async issueCliToken(): Promise<IssueCliTokenResponse> {
    const response: AxiosResponse<ApiResponse<IssueCliTokenResponse>> = await this.client.post('/v1/tokens/issue-cli');
    return response.data.data;
  }

  // Billing
  async getPlans(): Promise<Plan[]> {
    const response: AxiosResponse<ApiResponse<Plan[]>> = await this.client.get('/v1/billing/plans');
    return response.data.data;
  }

  async getMyBilling(): Promise<BillingMeResponse> {
    const response: AxiosResponse<ApiResponse<BillingMeResponse>> = await this.client.get('/v1/billing/me');
    // Normalize timestamp fields to ISO strings
    const data = response.data.data;
    const toIso = (val: any): string => {
      try {
        if (!val) return new Date().toISOString();
        if (val instanceof Date) return val.toISOString();
        if (typeof val === 'string' || typeof val === 'number') {
          const d = new Date(val);
          return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
        }
        if (typeof val === 'object') {
          if (typeof (val as any).toDate === 'function') {
            const d = (val as any).toDate();
            return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
          }
          if ('_seconds' in val || 'seconds' in val) {
            const seconds = (val as any)._seconds ?? (val as any).seconds;
            const nanos = (val as any)._nanoseconds ?? (val as any).nanoseconds ?? 0;
            const ms = (typeof seconds === 'number' ? seconds : 0) * 1000 + Math.floor(nanos / 1e6);
            const d = new Date(ms);
            return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
          }
        }
      } catch {}
      return new Date().toISOString();
    };
    return {
      plan: data.plan,
      usage: {
        ...data.usage,
        periodStart: toIso((data.usage as any).periodStart),
        periodEnd: toIso((data.usage as any).periodEnd),
      },
    };
  }

  // Subscriptions
  async getPricingTiers(): Promise<PricingTier[]> {
    const response: AxiosResponse<ApiResponse<{ tiers: PricingTier[] }>> = 
      await this.client.get('/v1/subscriptions/plans');
    return response.data.data.tiers;
  }

  async getCurrentSubscription(): Promise<CurrentSubscription> {
    const response: AxiosResponse<ApiResponse<CurrentSubscription>> =
      await this.client.get('/v1/subscriptions/current');
    return response.data.data;
  }

  async cancelSubscription(): Promise<{ message: string }> {
    const response: AxiosResponse<ApiResponse<{ message: string }>> =
      await this.client.post('/v1/subscriptions/cancel');
    return response.data.data;
  }

  async getPayments(limit?: number): Promise<{ payments: any[] }> {
    const response: AxiosResponse<ApiResponse<{ payments: any[] }>> =
      await this.client.get('/v1/subscriptions/payments', { params: { limit } });
    return response.data.data;
  }

  // Razorpay
  async createRazorpayOrder(tierId: string, billingCycle: 'monthly' | 'annual'): Promise<RazorpayOrderResponse> {
    const response: AxiosResponse<ApiResponse<RazorpayOrderResponse>> =
      await this.client.post('/v1/razorpay/create-order', { tierId, billingCycle });
    return response.data.data;
  }

  async verifyRazorpayPayment(payload: RazorpayVerifyPaymentRequest): Promise<RazorpayVerifyPaymentResponse> {
    const response: AxiosResponse<ApiResponse<RazorpayVerifyPaymentResponse>> =
      await this.client.post('/v1/razorpay/verify-payment', payload);
    return response.data.data;
  }

  // False Positives
  async reportFalsePositive(reportData: ReportFalsePositiveRequest): Promise<{ reportId: string; message: string; status: string }> {
    const response: AxiosResponse<ApiResponse<{ reportId: string; message: string; status: string }>> = 
      await this.client.post('/v1/false-positives', reportData);
    return response.data.data;
  }

  async getFalsePositiveReports(params?: {
    status?: string;
    scanId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: FalsePositiveReport[]; meta: any }> {
    const response: AxiosResponse<ApiResponse<FalsePositiveReport[]>> = 
      await this.client.get('/v1/false-positives', { params });
    return { data: response.data.data, meta: response.data.meta };
  }

  async getFalsePositiveReport(id: string): Promise<FalsePositiveReport> {
    const response: AxiosResponse<ApiResponse<FalsePositiveReport>> = 
      await this.client.get(`/v1/false-positives/${id}`);
    return response.data.data;
  }

  async deleteFalsePositiveReport(id: string): Promise<{ message: string }> {
    const response: AxiosResponse<ApiResponse<{ message: string }>> = 
      await this.client.delete(`/v1/false-positives/${id}`);
    return response.data.data;
  }
}

export const apiClient = new ApiClient();
